import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';
import { Icon } from '@components/icons';

const StyledProjectsGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};

  a {
    position: relative;
    z-index: 1;
  }
`;

const StyledProject = styled.li`
  position: relative;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(12, 1fr);
  align-items: center;
  ${({ theme }) => theme.mixins.boxShadow};

  @media (min-width: 769px) {
    box-shadow: none;
    transition: none;

    &:hover,
    &:focus-visible {
      box-shadow: none;
    }
  }

  &:not(:last-of-type) {
    margin-bottom: 30px;

    @media (min-width: 481px) {
      margin-bottom: 70px;
    }
    @media (min-width: 769px) {
      margin-bottom: 100px;
    }
  }

  &:nth-of-type(odd) {
    .video-content {
      grid-column: 1 / -1;
      padding: 25px 25px 20px;
      text-align: left;

      @media (min-width: 481px) {
        padding: 40px 40px 30px;
      }
      @media (min-width: 769px) {
        grid-column: 5 / -1;
        padding: 0;
        text-align: right;
      }
      @media (min-width: 1081px) {
        grid-column: 7 / -1;
      }
    }
    .video-tech-list {
      justify-content: flex-start;

      @media (min-width: 769px) {
        justify-content: flex-end;
      }

      li {
        margin: 0 10px 5px 0;

        @media (min-width: 769px) {
          margin: 0 0 5px 20px;
        }
      }
    }
    .video-links {
      justify-content: flex-start;
      margin-left: -10px;
      margin-right: 0;

      @media (min-width: 769px) {
        justify-content: flex-end;
        margin-left: 0;
        margin-right: -10px;
      }
    }
    .video-image {
      grid-column: 1 / -1;

      @media (min-width: 769px) {
        grid-column: 1 / 7;
      }
    }
  }

  .video-content {
    position: relative;
    grid-column: 1 / -1;
    grid-row: 1 / -1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    padding: 30px 25px 20px;
    z-index: 5;

    @media (min-width: 481px) {
      padding: 40px 40px 30px;
    }
    @media (min-width: 769px) {
      display: block;
      height: auto;
      grid-column: 1 / 9;
      padding: 0;
      z-index: auto;
    }
    @media (min-width: 1081px) {
      grid-column: 1 / 7;
    }
  }

  .video-overline {
    margin: 10px 0;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 400;
  }

  .video-title {
    color: var(--white);
    font-size: clamp(24px, 5vw, 28px);

    a {
      position: static;

      &:before {
        content: '';
        display: block;
        position: absolute;
        z-index: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }

    @media (min-width: 769px) {
      color: var(--lightest-slate);
      margin: 0 0 20px;

      a {
        position: relative;

        &:before {
          display: none;
        }
      }
    }
  }

  .video-description {
    position: relative;
    z-index: 2;
    padding: 20px 0;
    background-color: transparent;
    box-shadow: none;
    color: var(--light-slate);
    font-size: var(--fz-lg);

    &:hover {
      box-shadow: none;
    }

    @media (min-width: 769px) {
      ${({ theme }) => theme.mixins.boxShadow};
      padding: 25px;
      border-radius: var(--border-radius);
      background-color: var(--light-navy);
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    strong {
      color: var(--white);
      font-weight: normal;
    }
  }

  .video-tech-list {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
    margin: 10px 0;
    padding: 0;
    list-style: none;

    li {
      margin: 0 10px 5px 0;
      color: var(--lightest-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      white-space: nowrap;
    }

    @media (min-width: 769px) {
      margin: 25px 0 10px;

      li {
        margin: 0 20px 5px 0;
        color: var(--light-slate);
      }
    }
  }

  .video-links {
    display: flex;
    align-items: center;
    position: relative;
    margin-top: 10px;
    margin-left: -10px;
    color: var(--lightest-slate);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 10px;

      &.external {
        svg {
          width: 22px;
          height: 22px;
          margin-top: -4px;
        }
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }

  .video-image {
    ${({ theme }) => theme.mixins.boxShadow};
    grid-column: 1 / -1;
    grid-row: 1 / -1;
    position: relative;
    z-index: 1;
    height: 100%;
    opacity: 0.25;

    @media (min-width: 769px) {
      grid-column: 7 / -1;
      height: auto;
      opacity: 1;
    }
  }
`;

const Videos = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/videos/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              github
              external
              cta
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <section id="videos">
      <h2 className="numbered-heading" ref={revealTitle}>
        Videos
      </h2>

      <StyledProjectsGrid>
        {featuredProjects &&
          featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, github, cover, cta } = frontmatter;
            const image = getImage(cover);

            return (
              <StyledProject key={i} ref={el => (revealProjects.current[i] = el)}>
                <div className="video-content">
                  <div>
                    <h3 className="video-title">
                      <a href={external}>{title}</a>
                    </h3>

                    <div className="video-description" dangerouslySetInnerHTML={{ __html: html }} />

                    <div className="video-links">
                      {cta && (
                        <a href={cta} aria-label="Course Link" className="cta">
                          Learn More
                        </a>
                      )}
                      {github && (
                        <a href={github} aria-label="GitHub Link">
                          <Icon name="GitHub" />
                        </a>
                      )}
                      {external && !cta && (
                        <a href={external} aria-label="External Link" className="external">
                          <Icon name="External" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="video-image">
                  <a href={external ? external : github ? github : '#'}>
                    <GatsbyImage image={image} alt={title} className="img" />
                  </a>
                </div>
              </StyledProject>
            );
          })}
      </StyledProjectsGrid>
    </section>
  );
};

export default Videos;
